<template>
  <div>
    <v-progress-linear
      indeterminate
      color="primary"
      class="my-4"
      v-if="isFetching"
    ></v-progress-linear>
    <div class="actions">
      <v-btn
        variant="flat"
        color="primary"
        size="large"
        @click="() => isCreateDialogOpen = true"
        class="create-button"
      >
        <v-icon start>mdi-plus</v-icon>
        {{ $t('workScheduling.shiftPatternSettings.create') }}
      </v-btn>
    </div>
    <v-table class="mb-8">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">{{ $t('workScheduling.shiftPatternSettings.tableHeaders.name') }}</th>
            <th class="text-left">{{ $t('workScheduling.shiftPatternSettings.tableHeaders.abbreviation') }}</th>
            <th class="text-left">{{ $t('workScheduling.shiftPatternSettings.tableHeaders.color') }}</th>
            <th class="text-left"></th>
          </tr>
        </thead>
        
        <draggable 
          v-model="shiftPatterns" 
          tag="tbody" 
          item-key="shiftPatternId" 
          handle=".drag-handle"
        >
          <template #item="{ element: pattern }">
            <tr>
              <td>
                <v-icon class="drag-handle" size="small"> mdi-drag </v-icon>
                {{ pattern.name }}
              </td>
              <td>
                {{ pattern.abbreviation }}
              </td>
              <td>
                <div class="color-chip" :style="`background-color: ${pattern.color};`"></div>
              </td>
              <td>
                <v-row>
                  <v-btn
                    class="mx-2"
                    color="primary"
                    variant="outlined"
                    @click="setEditingPattern(pattern)"
                  >
                    {{ $t('common.edit') }}
                  </v-btn>
                  <v-btn
                    class="mx-2"
                    color="red"
                    variant="flat"
                    @click="() => deletingPattern = pattern"
                  >
                    {{ $t('common.delete') }}
                  </v-btn>
                </v-row>
              </td>
            </tr>
          </template>
        </draggable>
      </template>
    </v-table>
  
    <v-dialog max-width="720px" v-model="isCreateDialogOpen">
      <v-card>
        <v-card-title>{{ $t('workScheduling.shiftPatternSettings.createTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field
            class="mb-4"
            hide-details
            variant="outlined"
            :label="workSchedulingFormLabel('name')"
            v-model="newPatternTitle"
            :rules="[required]"
          />
          <v-text-field
            class="mb-4"
            hide-details
            variant="outlined"
            :label="workSchedulingFormLabel('abbreviation')"
            v-model="newPatternShorten"
            :rules="[required, isAbbreviationValid]"
          />
          <div>{{ $t('workScheduling.color') }}</div>
          <div class="mb-4">
            <color-picker v-model="newPatternColor" />
          </div>

          <div class="menu-header">{{ $t('workScheduling.shiftPatternSettings.form.timeSlot') }}</div>
          <div>
            <v-card
              v-for="(timetable, i) in newPatternTimeTables"
              :key="i"
              variant="outlined"
              class="pa-4 my-4"
            >
              <h4 class="mb-2">{{ $t('workScheduling.shiftPatternSettings.form.category') }}</h4>
              <v-select
                class="mb-4"
                :items="descriptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="compact"
                hide-details="auto"
                v-model="timetable.category"
                @update:model-value="updateCategoryInCreate($event, i)"
                color="primary"
              ></v-select>

              <div v-if="timetable.category === 'holiday'">
                <h4 class="mb-2">{{ $t('workScheduling.holidayReason') }}</h4>
                <v-text-field
                  class="mb-4"
                  hide-details="auto"
                  density="compact"
                  variant="outlined"
                  v-model="timetable.comment"
                  color="primary"
                />
              </div>

              <div v-if="timetable.category === 'workDescription'">
                <h4 class="mb-2">{{ $t('workScheduling.shiftPatternSettings.form.workDescription') }}</h4>
                <v-select
                  class="mb-4"
                  :items="workDescriptionOptions"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  v-model="timetable.workDescriptionId"
                  color="primary"
                ></v-select>
              </div>

              <h4 class="mb-2">{{ $t('workScheduling.shiftPatternSettings.form.time') }}</h4>
              <div class="d-flex align-center mb-4">
                <hour-minute-input v-model="timetable.startMin" />
                <div class="px-4">{{ $t('workScheduling.shiftPatternSettings.form.to') }}</div>
                <div class="d-flex">
                  <hour-minute-input v-model="timetable.endMin" />
                </div>
              </div>

              <div class="d-flex justify-center mt-4">
                <v-btn
                  variant="flat"
                  color="red"
                  @click="removeTimetable(i)"
                >
                  {{ $t('workScheduling.deleteTimeSlot') }}
                </v-btn>
              </div>
            </v-card>
          </div>

          <div class="d-flex justify-center my-4">
            <v-btn variant="flat" color="primary" @click="createNewTimeTable">
              {{ $t('workScheduling.addTimeSlot') }}
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            variant="flat"
            color="primary"
            @click="createShiftPattern"
            :disabled="!isNewPatternValid"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
        <v-progress-linear
          v-if="isCreatingNewPattern"
          indeterminate
          color="primary"
        ></v-progress-linear>
      </v-card>
    </v-dialog>
  
    <v-dialog max-width="720px" v-model="isEditDialogOpen">
      <v-card v-if="editingPattern">
        <v-card-title>{{ $t('workScheduling.shiftPatternSettings.editTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field
            class="mb-4"
            hide-details
            variant="outlined"
            :label="workSchedulingFormLabel('name')"
            v-model="editingPattern.name"
          />
          <v-text-field
            class="mb-4"
            hide-details
            variant="outlined"
            :label="workSchedulingFormLabel('abbreviation')"
            v-model="editingPattern.abbreviation"
          />
          <div>{{ $t('workScheduling.color') }}</div>
          <div class="mb-4">
            <color-picker v-model="editingPattern.color" />
          </div>

          <div class="menu-header">{{ $t('workScheduling.shiftPatternSettings.form.timeSlot') }}</div>
          <div>
            <v-card
              v-for="(timetable, i) in editingPattern.timetables"
              :key="i"
              variant="outlined"
              class="pa-4 my-4"
            >
              <h4 class="mb-2">{{ $t('workScheduling.shiftPatternSettings.form.category') }}</h4>
              <v-select
                class="mb-4"
                :items="descriptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="compact"
                hide-details="auto"
                v-model="timetable.category"
                @update:model-value="updateCategoryInEdit($event, i)"
              ></v-select>

              <div v-if="timetable.category === 'holiday'">
                <h4 class="mb-2">{{ $t('workScheduling.holidayReason') }}</h4>
                <v-text-field
                  class="mb-4"
                  hide-details="auto"
                  density="compact"
                  variant="outlined"
                  v-model="timetable.comment"
                />
              </div>

              <div v-if="timetable.category === 'workDescription'">
                <h4 class="mb-2">{{ $t('workScheduling.shiftPatternSettings.form.workDescription') }}</h4>
                <v-select
                  class="mb-4"
                  :items="workDescriptionOptions"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  v-model="timetable.workDescriptionId"
                  @update:model-value="selectWorkDescriptionInEdit($event, i)"
                ></v-select>
              </div>

              <h4 class="mb-2">{{ $t('workScheduling.shiftPatternSettings.form.time') }}</h4>
              <div class="d-flex align-center mb-4">
                <hour-minute-input v-model="timetable.startMin" />
                <div class="px-4">{{ $t('workScheduling.shiftPatternSettings.form.to') }}</div>
                <div class="d-flex">
                  <hour-minute-input v-model="timetable.endMin" />
                </div>
              </div>

              <v-text-field
                class="mb-4"
                hide-details
                variant="outlined"
                :label="workSchedulingFormLabel('name')"
                v-model="timetable.name"
                :rules="[required]"
              />
              <v-text-field
                class="mb-4"
                hide-details
                variant="outlined"
                :label="workSchedulingFormLabel('abbreviation')"
                v-model="timetable.abbreviation"
                :rules="[required, isAbbreviationValid]"
              />
              <div>{{ $t('workScheduling.color') }}</div>
              <div class="mb-4">
                <color-picker v-model="timetable.color" />
              </div>

              <div class="d-flex justify-center mt-4">
                <v-btn
                  variant="flat"
                  color="red"
                  @click="removeTimetableFromEditing(i)"
                >
                  {{ $t('workScheduling.deleteTimeSlot') }}
                </v-btn>
              </div>
            </v-card>
          </div>

          <div class="d-flex justify-center my-4">
            <v-btn variant="flat" color="primary" @click="insertTimetableToEditing">
              {{ $t('workScheduling.addTimeSlot') }}
            </v-btn>
          </div>
        </v-card-text>
        <v-progress-linear
          v-if="isUpdatingPattern"
          indeterminate
          color="primary"
          class="my-2"
        ></v-progress-linear>
        <v-card-actions class="justify-center">
          <v-btn variant="flat" color="primary" @click="updateShiftPattern">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="720px" v-model="isDeleteDialogOpen">
      <v-card v-if="deletingPattern">
        <v-card-title>{{ $t('common.delete') }}</v-card-title>
        <v-card-text>
          {{ $t('workScheduling.shiftPatternSettings.deleteConfirm', { name: deletingPattern.name }) }}
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            variant="flat"
            color="grey"
            class="mx-2"
            @click="() => deletingPattern = null"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn variant="flat" color="red" class="mx-2" @click="deletePattern">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
        <v-progress-linear
          v-if="isDeletingPattern"
          indeterminate
          color="primary"
        ></v-progress-linear>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import draggable from "vuedraggable";
import ColorPicker from '@/components/molecules/ColorPicker.vue';
import HourMinuteInput from '@/components/molecules/HourMinuteInput.vue'
import {
  shiftPatternModule,
  WorkDescriptions,
  createTimeTableRequest,
} from '@/store/viewModules/work-schedule/shiftPatternModule';
import {
  workDescriptionModule
} from '@/store/viewModules/work-schedule/workDescriptionModule';
import ApiShift, { ShiftTimeTableRequest, ShiftPatternUpdateRequest } from '@/api/ApiShift'
import { ShiftPatternSettingResponse, ShiftTimetableObject, ShiftTimetableSettingObject } from 'chaild-api/lib';
import ValidationMixin from '@/mixins/ValidationMixin';

interface DataType {
  isFetching: boolean;
  isCreateDialogOpen: boolean;
  newPatternTitle: string;
  newPatternShorten: string;
  newPatternColor: string;
  newPatternTimeTables: ShiftTimeTableRequest[];
  isCreatingNewPattern: boolean;
  hours: number[];
  minutes: number[];

  isUpdatingPattern: boolean;
  editingPattern: ShiftPatternUpdateRequest | null;

  isDeletingPattern: boolean;
  deletingPattern: ShiftPatternSettingResponse | null;
}

const hours = [...Array(12)].map((emp, i) => i)
const minutes = [...Array(4)].map((emp, i) => i * 15)
const descriptionKeys = Object.keys(WorkDescriptions)

export default defineComponent({
  components: {
    ColorPicker,
    HourMinuteInput,
    draggable,
  },
  mixins: [ValidationMixin],
  data: (): DataType => {
    return {
      isFetching: false,
      isCreateDialogOpen: false,
      newPatternTitle: '',
      newPatternShorten: '',
      newPatternColor: '#029ec0',
      newPatternTimeTables: [],
      isCreatingNewPattern: false,
      hours,
      minutes,

      isUpdatingPattern: false,
      editingPattern: null,

      isDeletingPattern: false,
      deletingPattern: null,
    };
  },
  computed: {
    isNewPatternValid() {
      return (
        this.newPatternTitle.length > 0 &&
        this.newPatternShorten.length > 0
      )
    },
    shiftPatterns: {
      get(): ShiftPatternSettingResponse[] {
        return [...shiftPatternModule.shiftPatterns];
      },
      async set(value: ShiftPatternSettingResponse[]) {
        if (value) {
          const shiftPatternIds = value.map((item) => item)
          shiftPatternModule.setShiftPatterns(shiftPatternIds)

          this.isFetching = true
          await shiftPatternModule.sortShiftPatterns()
          await shiftPatternModule.listShiftPatterns()
          this.isFetching = false
        }
      }
    },
    workDescriptions() {
      return workDescriptionModule.workDescriptions
    },
    workDescriptionOptions(): Record<string, any>[] {
      return this.workDescriptions.map((wd) => ({
        title: wd.name,
        value: wd.workDescriptionId
      }))
    },
    descriptions(): Record<string, any>[] {
      return descriptionKeys.map((key) => ({
        title: this.$t(WorkDescriptions[key]) as string,
        value: key
      }))
    },
    isEditDialogOpen: {
      get() {
        return this.editingPattern !== null
      },
      set(value: boolean) {
        if (!value) {
          this.editingPattern = null
        }
      }
    },
    isDeleteDialogOpen: {
      get() {
        return this.deletingPattern !== null
      },
      set(value: boolean) {
        if (!value) {
          this.deletingPattern = null
        }
      }
    },
  },
  methods: {
      workSchedulingFormLabel(field: string): string {
        return this.$t(`workScheduling.shiftPatternSettings.form.${field}`) as string;
      },
      createNewTimeTable() {
        const timetable = createTimeTableRequest();
        this.newPatternTimeTables.push(timetable)
      },
      removeTimetable(index: number) {
        this.newPatternTimeTables.splice(index, 1)
      },
      setEditingPattern(pattern: ShiftPatternSettingResponse) {
        this.editingPattern = {
          shiftPatternId: pattern.shiftPatternId,
          name: pattern.name,
          abbreviation: pattern.abbreviation,
          color: pattern.color,
          timetables: pattern.timetables.map((tt) => ({
            startMin: tt.startMin,
            endMin: tt.endMin,
            comment: tt.comment,
            workDescriptionId: tt.workDescription?.workDescriptionId,
            category: tt.category,
            name: tt.name,
            abbreviation: tt.abbreviation,
            color: tt.color
          }))
        }
      },
      async createShiftPattern() {
        if (this.isNewPatternValid) {
          this.isCreatingNewPattern = true
          const timetables = this.newPatternTimeTables.map((tt) => {
            if (tt.workDescriptionId) {
              const find = this.workDescriptions.find((wd) => wd.workDescriptionId === tt.workDescriptionId)
              if (find) {
                tt.name = find.name
                tt.abbreviation = find.abbreviation
                tt.color = find.color
              }
            }
            return tt
          })
          await shiftPatternModule.createShiftPattern({
            name: this.newPatternTitle,
            abbreviation: this.newPatternShorten,
            color: this.newPatternColor,
            timetables: timetables
          })
          this.isCreatingNewPattern = false
  
          this.isFetching = true
          await shiftPatternModule.sortShiftPatterns()
          await shiftPatternModule.listShiftPatterns()
          this.isFetching = false
  
          this.isCreateDialogOpen = false
          this.newPatternColor = '#029ec0'
          this.newPatternTitle = ''
          this.newPatternShorten = ''
          this.newPatternTimeTables = []
        }
      },
      async updateShiftPattern() {
        if (this.editingPattern) {
          this.isUpdatingPattern = true
          await shiftPatternModule.updateShiftPattern(this.editingPattern)
          this.isUpdatingPattern = false
          this.editingPattern = null
        }
      },
      insertTimetableToEditing() {
        if (this.editingPattern) {
          const timetable = createTimeTableRequest();
          (this.editingPattern.timetables as Array<ShiftTimetableSettingObject | ShiftTimeTableRequest>).push(timetable)
        }
      },
      removeTimetableFromEditing(index: number) {
        if (this.editingPattern) {
          this.editingPattern.timetables.splice(index, 1)
        }
      },
      async deletePattern() {
        if (this.deletingPattern) {
          this.isDeletingPattern = true
          await shiftPatternModule.deleteShiftPattern(this.deletingPattern.shiftPatternId)
          this.isDeletingPattern = false
          
          this.isFetching = true
          await shiftPatternModule.sortShiftPatterns()
          await shiftPatternModule.listShiftPatterns()
          this.isFetching = false
          
          this.deletingPattern = null
        }
      },
      updateCategoryInCreate(category: string, index: number) {
        if (index < this.newPatternTimeTables.length) {
          const tt = this.newPatternTimeTables[index]
          if (category !== 'workDescription') {
            const desc = this.descriptions.find(d => d.value === category)
            if (desc) {
              tt.name = desc.title
              tt.abbreviation = desc.title.substring(0, 4)
            }
          }
        }
      },
      updateCategoryInEdit(category: string, index: number) {
        if (this.editingPattern && index < this.editingPattern.timetables.length) {
          const tt = this.editingPattern.timetables[index]
          if (category !== 'workDescription') {
            const desc = this.descriptions.find(d => d.value === category)
            if (desc) {
              tt.name = desc.title
              tt.abbreviation = desc.title.substring(0, 4)
            }
          }
        }
      },
      selectWorkDescriptionInEdit(workDescriptionId: number, index: number) {
        if (this.editingPattern) {
          this.editingPattern.timetables = this.editingPattern.timetables.map((tt, i) => {
            if (i === index) {
              const find = this.workDescriptions.find((wd) => wd.workDescriptionId === workDescriptionId)
              if (find) {
                tt.name = find.name
                tt.abbreviation = find.abbreviation
                tt.color = find.color
              }
            }
            return tt
          })
        }
      }
    },
    async mounted() {
      this.isFetching = true
      await Promise.all([
        workDescriptionModule.listWorkDescriptions(),
        shiftPatternModule.listShiftPatterns()
      ])
      this.isFetching = false
    }
  })
  </script>
  <style lang="scss" scoped>
  .color-chip {
    height: 30px;
    width: 90px;
    border-radius: 4px;
    border: 1px solid #ccc;
  }
  
  .drag-handle {
    margin-bottom: 2px;
    vertical-align: middle;
    cursor: grab;
  }
  
  .drag-handle:active {
    cursor: grabbing;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    margin: 16px 0;
  }

  .create-button {
    min-width: 220px;
    white-space: nowrap;
  }
  </style>